import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";

import { DEFAULT_OG_IMAGE, PERSON_NAME } from "../site";
import { absUrl } from "./jsonLd";
import { openGraphTags, twitterTags } from "./socialTags";

/**
 * Helmet block for non-article pages (index and marketing pages): title,
 * description, canonical, robots, Open Graph, Twitter card and optional
 * page-specific JSON-LD. ArticleSEO is its sibling for article bodies.
 */
const PageSEO = ({
	title,
	description = undefined,
	path = undefined,
	robots = undefined,
	image = DEFAULT_OG_IMAGE,
	twitterDescription = undefined,
	type = "website",
	noIndex = false,
	jsonLd = undefined,
}) => {
	const canonical = path ? absUrl(path) : null;
	const robotsContent =
		robots ||
		(noIndex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large");

	return (
		<Helmet>
			<title>{title}</title>
			{description && <meta name="description" content={description} />}
			{canonical && <link rel="canonical" href={canonical} />}
			<meta name="robots" content={robotsContent} />
			<meta name="author" content={PERSON_NAME} />
			{openGraphTags({ title, description, image, url: canonical, type })}
			{twitterTags({ title, description: twitterDescription || description, image })}
			{jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
		</Helmet>
	);
};

PageSEO.propTypes = {
	title: PropTypes.string.isRequired,
	description: PropTypes.string,
	/** Site-relative path, e.g. "/journal". Omit to skip the canonical link. */
	path: PropTypes.string,
	/** Raw robots directive, overriding the noIndex shorthand - e.g. "noindex, follow" for a half-written page. */
	robots: PropTypes.string,
	image: PropTypes.string,
	/** Twitter copy is often punchier than the meta description; falls back to it. */
	twitterDescription: PropTypes.string,
	type: PropTypes.string,
	noIndex: PropTypes.bool,
	/** Page-specific structured data, serialised into a ld+json script tag. */
	jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default PageSEO;
